import { Controller } from "@hotwired/stimulus";

const SVG_NS = "http://www.w3.org/2000/svg";
const WIDTH = 720;
const HEIGHT = 240;
const MARGIN = { top: 18, right: 24, bottom: 38, left: 42 };

export default class extends Controller {
  static targets = ["svg", "tooltip"];
  static values = {
    points: Array,
  };

  connect() {
    this.render();
  }

  disconnect() {
    this.hideTooltip();
  }

  render() {
    this.points = this.pointsValue || [];
    this.svgTarget.replaceChildren();
    this.svgTarget.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);

    if (this.points.length === 0) {
      this.renderEmptyState();
      return;
    }

    this.chartPoints = this.buildChartPoints();

    this.renderDefinitions();
    this.renderGrid();
    this.renderArea();
    this.renderLine();
    this.renderPoints();
    this.renderAxisLabels();
    this.renderActiveMarker();
    this.renderInteractionLayer();
    this.showPoint(this.chartPoints[this.chartPoints.length - 1], false);
  }

  showNearestPoint(event) {
    const point = this.nearestPointFor(event);
    this.showPoint(point);
  }

  showPoint(point, showTooltip = true) {
    if (!point) return;

    this.activeLine.setAttribute("x1", point.x);
    this.activeLine.setAttribute("x2", point.x);
    this.activeCircle.setAttribute("cx", point.x);
    this.activeCircle.setAttribute("cy", point.y);
    this.activeGroup.classList.remove("hidden");

    if (showTooltip) this.showTooltip(point);
  }

  hideTooltip() {
    if (!this.hasTooltipTarget) return;

    this.tooltipTarget.classList.add("hidden");
  }

  buildChartPoints() {
    const max = Math.max(...this.points.map((point) => point.count), 1);
    const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
    const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
    const step = this.points.length > 1 ? plotWidth / (this.points.length - 1) : 0;

    return this.points.map((point, index) => {
      const x = MARGIN.left + (step * index);
      const y = MARGIN.top + plotHeight - ((point.count / max) * plotHeight);

      return { ...point, x, y };
    });
  }

  renderDefinitions() {
    const defs = this.elementFor("defs");
    const gradient = this.elementFor("linearGradient", {
      id: "subscriptions-area-gradient",
      x1: "0",
      x2: "0",
      y1: "0",
      y2: "1",
    });

    gradient.append(
      this.elementFor("stop", { offset: "5%", "stop-color": "#0094FF", "stop-opacity": "0.28" }),
      this.elementFor("stop", { offset: "92%", "stop-color": "#0094FF", "stop-opacity": "0.02" }),
    );
    defs.append(gradient);
    this.svgTarget.append(defs);
  }

  renderGrid() {
    const group = this.elementFor("g", { "aria-hidden": "true" });
    const bottom = HEIGHT - MARGIN.bottom;

    [MARGIN.top, MARGIN.top + ((bottom - MARGIN.top) / 2), bottom].forEach((y) => {
      group.append(this.elementFor("line", {
        x1: MARGIN.left,
        x2: WIDTH - MARGIN.right,
        y1: y,
        y2: y,
        stroke: "#E7E7E7",
        "stroke-dasharray": "4 6",
      }));
    });

    this.svgTarget.append(group);
  }

  renderArea() {
    const bottom = HEIGHT - MARGIN.bottom;
    const path = [
      `M ${this.chartPoints[0].x} ${bottom}`,
      ...this.chartPoints.map((point) => `L ${point.x} ${point.y}`),
      `L ${this.chartPoints[this.chartPoints.length - 1].x} ${bottom}`,
      "Z",
    ].join(" ");

    this.svgTarget.append(this.elementFor("path", {
      d: path,
      fill: "url(#subscriptions-area-gradient)",
    }));
  }

  renderLine() {
    const path = this.chartPoints.map((point, index) => {
      return `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`;
    }).join(" ");

    this.svgTarget.append(this.elementFor("path", {
      d: path,
      fill: "none",
      stroke: "#0094FF",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "stroke-width": "4",
    }));
  }

  renderPoints() {
    const group = this.elementFor("g");

    this.chartPoints.forEach((point) => {
      const circle = this.elementFor("circle", {
        cx: point.x,
        cy: point.y,
        r: "4",
        fill: "#FFFFFF",
        stroke: "#0065B2",
        "stroke-width": "3",
        tabindex: "0",
        role: "button",
        "aria-label": `${point.long_label}: ${point.count} new subscriptions`,
      });

      circle.addEventListener("focus", () => this.showPoint(point));
      circle.addEventListener("blur", () => this.hideTooltip());
      group.append(circle);
    });

    this.svgTarget.append(group);
  }

  renderAxisLabels() {
    const labels = this.elementFor("g", {
      fill: "#666666",
      "font-size": "12",
      "aria-hidden": "true",
    });
    const firstPoint = this.chartPoints[0];
    const lastPoint = this.chartPoints[this.chartPoints.length - 1];
    const max = Math.max(...this.points.map((point) => point.count), 0);

    labels.append(
      this.textFor(firstPoint.label, { x: MARGIN.left, y: HEIGHT - 8 }),
      this.textFor(lastPoint.label, { x: WIDTH - MARGIN.right, y: HEIGHT - 8, "text-anchor": "end" }),
      this.textFor(max, { x: WIDTH - MARGIN.right, y: MARGIN.top + 4, "text-anchor": "end" }),
    );

    this.svgTarget.append(labels);
  }

  renderActiveMarker() {
    this.activeGroup = this.elementFor("g", { class: "hidden", "aria-hidden": "true" });
    this.activeLine = this.elementFor("line", {
      y1: MARGIN.top,
      y2: HEIGHT - MARGIN.bottom,
      stroke: "#0065B2",
      "stroke-dasharray": "4 5",
      "stroke-width": "2",
    });
    this.activeCircle = this.elementFor("circle", {
      r: "7",
      fill: "#FFFFFF",
      stroke: "#0065B2",
      "stroke-width": "3",
    });

    this.activeGroup.append(this.activeLine, this.activeCircle);
    this.svgTarget.append(this.activeGroup);
  }

  renderInteractionLayer() {
    const rect = this.elementFor("rect", {
      x: MARGIN.left,
      y: MARGIN.top,
      width: WIDTH - MARGIN.left - MARGIN.right,
      height: HEIGHT - MARGIN.top - MARGIN.bottom,
      fill: "transparent",
      "data-interaction-layer": "true",
    });

    rect.addEventListener("pointerenter", (event) => this.showNearestPoint(event));
    rect.addEventListener("pointermove", (event) => this.showNearestPoint(event));
    rect.addEventListener("pointerdown", (event) => this.showNearestPoint(event));
    rect.addEventListener("pointerleave", () => this.hideTooltip());
    this.svgTarget.append(rect);
  }

  nearestPointFor(event) {
    const pointer = this.svgPointFor(event.clientX, event.clientY);
    if (!pointer) return;

    return this.chartPoints.reduce((nearestPoint, point) => {
      return Math.abs(point.x - pointer.x) < Math.abs(nearestPoint.x - pointer.x) ? point : nearestPoint;
    }, this.chartPoints[0]);
  }

  showTooltip(point) {
    if (!this.hasTooltipTarget) return;

    const anchor = this.screenPointFor(point.x, point.y);
    if (!anchor) return;

    this.tooltipTarget.replaceChildren(
      this.tooltipLine(point.long_label, "font-bold text-black"),
      this.tooltipLine(`${point.count} new subscriptions`, "text-gray"),
    );
    this.tooltipTarget.classList.remove("hidden");

    const containerRect = this.element.getBoundingClientRect();
    const tooltipRect = this.tooltipTarget.getBoundingClientRect();
    const left = anchor.x - containerRect.left;
    const top = anchor.y - containerRect.top;
    const clampedLeft = Math.min(
      Math.max(left, (tooltipRect.width / 2) + 8),
      containerRect.width - (tooltipRect.width / 2) - 8,
    );

    this.tooltipTarget.style.left = `${clampedLeft}px`;
    this.tooltipTarget.style.top = `${Math.max(top - 12, tooltipRect.height + 8)}px`;
    this.tooltipTarget.style.transform = "translate(-50%, -100%)";
  }

  svgPointFor(x, y) {
    const screenMatrix = this.svgTarget.getScreenCTM();
    if (!screenMatrix) return;

    return this.pointFor(x, y).matrixTransform(screenMatrix.inverse());
  }

  screenPointFor(x, y) {
    const screenMatrix = this.svgTarget.getScreenCTM();
    if (!screenMatrix) return;

    return this.pointFor(x, y).matrixTransform(screenMatrix);
  }

  pointFor(x, y) {
    const point = this.svgTarget.createSVGPoint();
    point.x = x;
    point.y = y;
    return point;
  }

  tooltipLine(text, className) {
    const line = document.createElement("p");
    line.className = className;
    line.textContent = text;
    return line;
  }

  renderEmptyState() {
    this.svgTarget.append(this.textFor("No data", {
      x: WIDTH / 2,
      y: HEIGHT / 2,
      fill: "#666666",
      "font-size": "14",
      "text-anchor": "middle",
    }));
  }

  textFor(text, attributes = {}) {
    const element = this.elementFor("text", attributes);
    element.textContent = text;
    return element;
  }

  elementFor(name, attributes = {}) {
    const element = document.createElementNS(SVG_NS, name);

    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));

    return element;
  }
}
